// Service imports:
import { turnDateToSimpleTime } from '../../services/utils';
import { fetchResponseToDetailedString, bugsnagNotify } from '../../services/bugsnagUtils';
// Query imports:
import { requestPrivateOrdersAndTradesQuery } from '../queries/privateQueries';
// Type imports:
import { AppDispatch } from '../../index'; // Use the root dispatch type from your store
import { IPrivateGeniumInetTradeReport, IPrivateInetTradeReport, TradingGateway } from '../../types/api/PrivateTypes';
import { PrivateActions, CLEAR_FAILOVER, REQUEST_PRIVATE_ORDERS_AND_TRADES_FAILURE, REQUEST_PRIVATE_ORDERS_AND_TRADES_SUCCESS, REQUEST_PRIVATE_ORDERS_AND_TRADES_WAITING } from '../../types/redux/PrivateTypes';
import { IPrivateOrder, IPrivateTrade } from '../../types/api/PrivateTypes';
import { GET_SIGTI_API_URL } from '../../config';
import { REQUEST_FORBIDDEN } from '../../types/redux/LoginTypes';

// ================================ CHECK IF FORBIDDEN RESOURCE (LOGOUT IF TRUE) ================================
const checkIfForbiddenResource = (responseBody: any) => {
    if (responseBody.errors && responseBody.errors[0].message === "Forbidden resource") {
        return true;
    }
    return false;
}

const requestForbidden = () => ({
    type: REQUEST_FORBIDDEN,
});

/* REQUEST PRIVATE ORDERS */
// ========================================================== GET PRIVATE DATA EMS ==========================================================

// Fetches private data for a single gateway and returns the parsed payload, or null on error.
const fetchPrivateDataForGateway = async (
    url: string,
    query: string,
    symbol: string,
    simpleDateString: string,
    tradingGateway: TradingGateway,
    dispatch: AppDispatch
): Promise<{ orders: IPrivateOrder[], trades: IPrivateTrade[], inetTradeReports: IPrivateInetTradeReport[], geniuminetTradeReports: IPrivateGeniumInetTradeReport[] } | null> => {
    const queryObject = {
        query,
        variables: {
            tradingGateway,
            symbol,
            fromDate: new Date(simpleDateString),
            toDate: new Date(simpleDateString)
        }
    };
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (!response.ok) return null;
        const responseBody = await response.json();
        // Forbidden resource — log the user out immediately.
        if (checkIfForbiddenResource(responseBody)) {
            dispatch(requestForbidden());
            return null;
        }
        const { orders = [], trades = [], inetTradeReports = [], geniuminetTradeReports = [] } =
            responseBody?.data?.privateData || {};
        return { orders, trades, inetTradeReports, geniuminetTradeReports };
    } catch {
        return null;
    }
};

const requestPrivateOrdersAndTradesThunk = async (dispatch: AppDispatch, symbol: string, currentTradingDay: string, accessToken: string, tradingGateway: TradingGateway) => {

    // Format time string.
    const simpleDateString = turnDateToSimpleTime(new Date(currentTradingDay));

    // Set as waiting for data.
    dispatch(requestPrivateOrdersAndTradesWaiting(symbol, simpleDateString));

    const url = GET_SIGTI_API_URL();
    const query = requestPrivateOrdersAndTradesQuery();

    try {
        // For bonds, the gateway can be either 'fusion' or 'geniuminet'.
        // Fire both requests in parallel and merge the results.
        if (tradingGateway === 'fusion') {
            const [fusionResult, geniuminetResult] = await Promise.all([
                fetchPrivateDataForGateway(url, query, symbol, simpleDateString, 'fusion', dispatch),
                fetchPrivateDataForGateway(url, query, symbol, simpleDateString, 'geniuminet', dispatch),
            ]);

            if (!fusionResult && !geniuminetResult) {
                dispatch(requestPrivateOrdersAndTradesFailure(symbol, simpleDateString, 'Request Failed'));
                return;
            }

            const orders            = [...(fusionResult?.orders ?? []),                ...(geniuminetResult?.orders ?? [])];
            const trades            = [...(fusionResult?.trades ?? []),                ...(geniuminetResult?.trades ?? [])];
            const inetTradeReports  = [...(fusionResult?.inetTradeReports ?? []),     ...(geniuminetResult?.inetTradeReports ?? [])];
            const geniuminetReports = [...(fusionResult?.geniuminetTradeReports ?? []), ...(geniuminetResult?.geniuminetTradeReports ?? [])];

            dispatch(requestPrivateOrdersAndTradesSuccess(symbol, simpleDateString, orders, trades, inetTradeReports, geniuminetReports));
            return;
        }

        // For non-bond instruments (inet) use a single request as before.
        const queryObject = {
            query,
            variables: {
                tradingGateway,
                symbol,
                fromDate: new Date(simpleDateString),
                toDate: new Date(simpleDateString)
            }
        };

        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });

        if (response.ok) {
            const responseBody = await response.json();

            // Hard error: forbidden resource — log the user out regardless of data.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
                return;
            }
            if (!responseBody?.data?.privateData) {
                const errorMessage = responseBody?.errors?.[0]?.message ?? 'Request Failed';
                dispatch(requestPrivateOrdersAndTradesFailure(symbol, simpleDateString, errorMessage));
                return;
            }

            const { 
                orders = [], 
                trades = [], 
                inetTradeReports = [], 
                geniuminetTradeReports = [] 
              } = responseBody.data.privateData;

            dispatch(requestPrivateOrdersAndTradesSuccess(symbol, simpleDateString, orders, trades, inetTradeReports, geniuminetTradeReports));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Private Orders And Trades', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestPrivateOrdersAndTradesFailure(symbol, simpleDateString, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestPrivateOrdersAndTradesFailure(symbol, simpleDateString, 'Network Error'));
    }
}

const requestPrivateOrdersAndTradesWaiting = (
    symbol: string, 
    tradingDay: string
): PrivateActions => {
    return {
        type: REQUEST_PRIVATE_ORDERS_AND_TRADES_WAITING,
        payload: {
            symbol,
            tradingDay
        }
    };
};

const requestPrivateOrdersAndTradesSuccess = (
    symbol: string,
    tradingDay: string,
    orders: IPrivateOrder[],
    trades: IPrivateTrade[],
    inetTradeReports: IPrivateInetTradeReport[],
    geniumInetTradeReports: IPrivateGeniumInetTradeReport[]
): PrivateActions => {
    return {
        type: REQUEST_PRIVATE_ORDERS_AND_TRADES_SUCCESS,
        payload: {
            symbol,
            tradingDay,
            orders,
            trades,
            inetTradeReports,
            geniumInetTradeReports
        }
    };
};

const requestPrivateOrdersAndTradesFailure = (
    symbol: string,
    tradingDay: string,
    error: string,
): PrivateActions => {
    return {
        type: REQUEST_PRIVATE_ORDERS_AND_TRADES_FAILURE,
        payload: {
            symbol,
            tradingDay,
            error,
        }
    };
};

export const requestPrivateOrdersAndTrades =
    (symbol: string, currentTradingDay: string, accessToken: string, tradingGateway: TradingGateway) =>
    (dispatch: AppDispatch) =>
        requestPrivateOrdersAndTradesThunk(dispatch, symbol, currentTradingDay, accessToken, tradingGateway);

/* CLEAR FAILOVER */
export const clearFailover = () => (dispatch: AppDispatch) => {
    dispatch({ type: CLEAR_FAILOVER });
};